import { type ActionEvent } from "@hotwired/stimulus"

import DrawerController from "../drawer_controller"
import DrawerGroupController from "../drawer_group_controller"
import ModalController from "../modal_controller"
import SearchDateController from "../search/search_date_controller"
import SearchExperienceController from "../search/search_experience_controller"
import SearchGuestsController from "@/controllers/search/search_guests_controller"

import { saveNavigationTypeToStorage } from "@/helpers/ga4_helper"
import { setInputValueWithEvents } from "@/helpers/utils"

export default class extends ModalController {
  static outlets = ["drawer", "drawer-group", "search-date", "search-experience", "search-guests"]
  declare readonly drawerOutlets: DrawerController[]
  declare readonly drawerGroupOutlet: DrawerGroupController
  declare readonly searchDateOutlet: SearchDateController
  declare readonly searchExperienceOutlet: SearchExperienceController
  declare readonly searchGuestsOutlet: SearchGuestsController

  static targets = [
    "calendarContainer",
    "calendarHeader",
    "calendarMonth",
    "clearAllButton",
    "dateRadio",
    "experienceSummary",
    "guestsSummary",
    "locationInput",
    "locationSummary"
  ]

  declare readonly calendarContainerTarget: HTMLElement
  declare readonly calendarHeaderTarget: HTMLElement
  declare readonly calendarMonthTargets: HTMLTableElement[]
  declare readonly clearAllButtonTarget: HTMLElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly experienceSummaryTarget: HTMLElement
  declare readonly guestsSummaryTarget: HTMLElement
  declare readonly locationInputTarget: HTMLInputElement
  declare readonly locationSummaryTarget: HTMLElement

  clearAll(): void {
    setInputValueWithEvents(this.locationInputTarget, "")
    this.searchDateOutlet.deselectAllDates()
    this.searchExperienceOutlet.resetExperience()
    this.searchGuestsOutlet.resetGuests()
    this.drawerOutlets.forEach((drawer) => drawer.close())
    this.clearAllButtonTarget.hidden = true
  }

  protected performOpen(shouldAnimate: boolean): void {
    super.performOpen(shouldAnimate)

    // Set custom prop for calendar scroll offset equal to height of sticky header, plus gap.
    this.element.style.setProperty(
      "--search-modal-date-offset",
      `${this.calendarHeaderTarget.getBoundingClientRect().height + 16}px`
    )

    if (shouldAnimate) {
      this.updateClearAllVisibility()
    }

    // Open first drawer that's missing a value.
    if (!this.currentOpenDrawer) {
      this.openNextEmptyDrawer()
    }
  }

  openDateDrawer(): void {
    // Select appropriate tab and scroll to selected date before opening drawer to reduce jumping.
    this.searchDateOutlet.selectCalendarTab()
    this.jumpToSelectedDate()
    this.openDrawer(this.dateDrawer)
  }

  openExperienceDrawer(): void {
    this.openDrawer(this.experienceDrawer)
  }

  openGuestsDrawer(): void {
    this.openDrawer(this.guestsDrawer)
  }

  openLocationDrawer(): void {
    this.openDrawer(this.locationDrawer)
  }

  openNextEmptyDrawer(): void {
    const { currentOpenDrawer } = this

    const hasLocation = !!this.locationInputTarget.value
    const hasDate = this.searchDateOutlet.isDateSelected
    const hasExperience = !this.searchExperienceOutlet.isDefaultSelected

    const followingDrawers = currentOpenDrawer
      ? this.drawerOutlets.filter(
          (drawer) =>
            currentOpenDrawer.element.compareDocumentPosition(drawer.element) &
            Node.DOCUMENT_POSITION_FOLLOWING
        )
      : this.drawerOutlets

    if (!hasLocation && followingDrawers.includes(this.locationDrawer)) {
      this.openLocationDrawer()
    } else if (!hasDate && followingDrawers.includes(this.dateDrawer)) {
      this.openDateDrawer()
    } else if (!hasExperience && followingDrawers.includes(this.experienceDrawer)) {
      this.openExperienceDrawer()
    } else {
      this.openGuestsDrawer()
    }
  }

  setExperienceSummary({ params: { name } }: ActionEvent): void {
    this.experienceSummaryTarget.textContent = name
    this.updateClearAllVisibility()
  }

  setGuestsSummary(): void {
    this.guestsSummaryTarget.textContent = this.searchGuestsOutlet.summaryText
    this.updateClearAllVisibility()
  }

  setLocationSummary({ currentTarget }: ActionEvent): void {
    this.locationSummaryTarget.textContent = (currentTarget as HTMLInputElement).value || "Any"
    this.updateClearAllVisibility()
  }

  submit(): void {
    saveNavigationTypeToStorage("search_box")
  }

  updateClearAllVisibility(): void {
    const hasLocation = !!this.locationInputTarget.value
    const hasDate = this.searchDateOutlet.isDateSelected
    const hasExperience = !this.searchExperienceOutlet.isDefaultSelected
    const hasGuests = !this.searchGuestsOutlet.isZeroSelected

    const selectedCount = [hasLocation, hasDate, hasExperience, hasGuests].filter(Boolean).length
    this.clearAllButtonTarget.hidden = selectedCount < 2
  }

  private get currentOpenDrawer(): DrawerController | undefined {
    return this.drawerOutlets.find((drawer) => drawer.openValue)
  }

  private get dateDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-date]")
  }

  private drawerFor(searchInputControllerSelector: string): DrawerController {
    return this.drawerOutlets.find(
      (drawer) =>
        !!drawer.element.querySelector(searchInputControllerSelector) ||
        !!drawer.element.closest(searchInputControllerSelector)
    )!
  }

  private get experienceDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-experience]")
  }

  private get guestsDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-guests]")
  }

  private jumpToSelectedDate(): void {
    const month = this.selectedDateRadio
      ? this.calendarMonthTargets.find((month) => month.contains(this.selectedDateRadio!))
      : this.calendarMonthTargets.at(0)

    // We use `scrollTop` on parent instead of `scrollIntoView` on element because the latter
    // will try to scroll ancestor containers rather than the immediate parent, and we can't
    // hide overflow on those ancestors without breaking the auto drawer height that fills
    // the available modal space.
    this.calendarContainerTarget.scrollTop =
      month!.offsetTop - this.calendarContainerTarget.offsetTop
  }

  private get locationDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-location]")
  }

  private openDrawer(drawer: DrawerController): void {
    // Set custom prop for drawer height offset equal to height of previous drawers, plus padding.
    const PADDING = 8
    const drawerGap = parseInt(
      window.getComputedStyle(this.drawerGroupOutlet.element).getPropertyValue("gap")
    )

    const drawerIndex = this.drawerOutlets.indexOf(drawer)
    const offset = drawer.element.getBoundingClientRect().height + drawerGap
    this.element.style.setProperty(
      "--search-modal-height-offset",
      `${offset * drawerIndex + PADDING}px`
    )

    drawer.open()
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }
}
